import { CTInput, CTButton, CTDatePicker, CTCard, CTLabel, CTProgressCircular } from "@nbcuniversal/ct-ui";
import React, { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useNavigate } from "react-router-dom";

import { API_PATH } from "../../../Configurations";
import usePageTitle from "../../../hooks/UsePageTitle";
import checkMaintenanceMode from "../../../MaintenanceModeUtil";
import "./ForgotUsernameForm.scss";

interface ForgotUsernamePost {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  last4OfSocial: string;
  sso?: string;
}

class RecaptchaError extends Error {
  public constructor(message: string, public originalError?: unknown) {
    super(message);
    this.name = "RecaptchaError";
  }
}

const ForgotUsernameForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  usePageTitle("Alumni Forgot Username");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [last4OfSocial, setLast4OfSocial] = useState("");
  const [nbcSso, setNbcSso] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [shouldDisplaySSN, setShouldDisplaySSN] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [requestErrorMsg, setRequestErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [censoredEmail, setCensoredEmail] = useState("");

  const handleRecaptchaVerification = async (): Promise<string> => {
    if (!executeRecaptcha) {
      console.error("reCAPTCHA not initialized. executeRecaptcha method not available.");
      throw new RecaptchaError("reCAPTCHA service unavailable");
    }

    try {
      const token = await executeRecaptcha("submit");

      if (!token) {
        throw new RecaptchaError("Failed to generate reCAPTCHA token");
      }

      return token;
    }
    catch (error) {
      console.error("reCAPTCHA verification failed:", error);
      if (error instanceof RecaptchaError) {
        throw error;
      }
      throw new RecaptchaError("reCAPTCHA verification failed. Please try again.", error);
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setRequestError(false);
    setRequestErrorMsg("");
    let currentRequestError = false;
    try {
      const token = await handleRecaptchaVerification();

      const data: ForgotUsernamePost = {
        firstName,
        lastName,
        dateOfBirth,
        last4OfSocial,
      };
      // optional and only provide if the value is set
      if (nbcSso) {
        data.sso = nbcSso;
      }
      // Check we are not in maintenance prior to posts..
      if (await checkMaintenanceMode() === true) {
        setIsLoading(false);
        currentRequestError = true;
        setRequestError(currentRequestError);
        const msg = "Portal is in Maintenance Mode";
        setRequestErrorMsg(msg);
        throw new Error(msg);
      }

      const response = await fetch(`${API_PATH}/registration/forgot-username`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-recaptcha-token": token,
        },
        body: JSON.stringify(data),
      });

      if (response.status === 403) {
        const deniedRequestMessage = "You have been detected as a robot. Please use a different browser or try again later.";
        currentRequestError = true;
        setRequestError(currentRequestError);
        setRequestErrorMsg(deniedRequestMessage);
        console.error(deniedRequestMessage);
        setIsLoading(false);
        return;
      }

      const result = await response.json();

      if (result.maskedEmail) {
        setCensoredEmail(result.maskedEmail);
        setIsLoading(false);
      }
      else {
        setRequestError(true);
        if (result.message !== "" && result.message !== undefined) {
          setRequestErrorMsg(result.message);
        }
        else {
          setRequestErrorMsg("No account was found matching the information provided.");
        }
        setIsLoading(false);
        return;
      }
    }
    catch (error: any) {
      // Check if this is a reCAPTCHA specific error
      if (error instanceof RecaptchaError) {
        setRequestError(true);
        setRequestErrorMsg("Security verification failed. Please refresh the page and try again.");
        console.error("reCAPTCHA error:", error);
      }
      else if (currentRequestError === false) {
        setRequestError(true);
        setRequestErrorMsg("There was an Error processing your request.");
      }
      console.error("There was an error!", error);
      setIsLoading(false);
    }
  };

  const reformatForDatePicker = (date: string): Date => {
    const [year, month, day] = date.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  return (
    <>
      {!isLoading && !censoredEmail && (
        <div className={"ct-flex ct-justify-center ct-items-center cardContainer"}>
          <CTCard expandable={false} className='ct-px-1 ct-mb-4'>
            <CTCard.Header className="ct-px-4 ct-py-2 ct-border-t-[5px] ct-border-t-primary-500 ct-bg-cloudy-gray-500 ct-h-[75px]">
              <div className={"ct-flex ct-justify-between ct-items-center"} >
                <h1 className="ct-text-primary-500 ct-text-[22px] ct-font-bold ct-m-0 ct-leading-[1.5] ct-font-rock ct-text-nowrap">
                  {"Forgot Your Username?"}
                </h1>
              </div>
            </CTCard.Header>
            <CTCard.Content className='ct-font-sans'>
              <div className="ct-p-4 ct-space-y-1">
                <p className="ct-mb-[30px]">
                  Enter the personal information you shared with your employer to retrieve your username.
                </p>
                <div className="formLayout">
                  {/* First Name */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="firstName">First Name*</CTLabel>
                  <CTInput id="firstName" disabled={isLoading} value={firstName} onChange={(e: string) => {
                    setFirstName(e);
                  }} containerClassName='ct-col-span-9' type="text" />

                  {/* Last Name */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="lastName">Last Name*</CTLabel>
                  <CTInput id="lastName" disabled={isLoading} value={lastName} onChange={(e: string) => {
                    setLastName(e);
                  }} containerClassName='ct-col-span-9' type="text" />

                  {/* Last 4 digits of SSN */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="ssn">Last 4 digits of SSN*</CTLabel>
                  <div className='ct-col-span-9 ct-flex-1 ct-flex ct-items-center'>
                    <CTInput id="ssn" visibleNumberOfChars={0} disabled={isLoading} value={last4OfSocial} maxLength={4} onChange={(e: string) => {
                      setLast4OfSocial(e);
                    }} containerClassName="ct-flex-1" className="ct-rounded-none ct-rounded-l" type={shouldDisplaySSN ? "text" : "password"} />
                    <CTButton
                      className='ct-flex-none ct-h-[40px] ct-w-[38px] ct-border-solid ct-border-0 ct-border-r ct-border-b ct-border-t
                        ct-bg-storm-gray-0 ct-text-storm-gray-700  ct-border-iron-gray-100 ct-rounded-none ct-rounded-r hover:ct-bg-storm-gray-0'
                      aria-label={shouldDisplaySSN ? "Hide SSN" : "Show SSN"}
                      iconSize={15}
                      icon={shouldDisplaySSN ? "eye" : "eye-slash"}
                      onClick={() => setShouldDisplaySSN(!shouldDisplaySSN)} >
                    </CTButton>
                  </div>

                  {/* Date of Birth */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="dob">DOB*</CTLabel>
                  <div className="ct-col-span-9 ct-flex-grow ct-items-center">
                    <CTDatePicker
                      id={"dob"}
                      disabled={isLoading}
                      maxDate={new Date()}
                      selected={dateOfBirth == "" ? null : reformatForDatePicker(dateOfBirth)}
                      dateFormat='MM/dd/yyyy'
                      onChange={(date: Date | null) => {
                        let formattedDate = "";
                        if (date) {
                          const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate().toString();
                          const month = (date.getMonth() + 1) < 10 ? `0${date.getMonth() + 1}` : (date.getMonth() + 1).toString();
                          const year = date.getFullYear();
                          formattedDate = `${year}-${month}-${day}`;
                        }

                        setDateOfBirth(formattedDate);
                      }}
                    />
                  </div>

                  {/* SSO ID */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="nbcSso">SSO ID</CTLabel>
                  <CTInput id="nbcSso" disabled={isLoading} value={nbcSso} onChange={(e: string) => {
                    setNbcSso(e);
                  }} className="ct-w-full" containerClassName='ct-col-span-9' type="text" />
                </div>

                {/* Submit Section */}
                <div className="md:ct-flex md:ct-flex-row md:ct-items-end md:ct-justify-between sm:ct-flex-col sm:ct-flex">
                  <div className="ct-flex ct-justify-end ct-mt-1">
                    <CTButton
                      disabled={!dateOfBirth || !firstName || !lastName || !last4OfSocial || isLoading}
                      onClick={onSubmit} primary
                      raised>Submit</CTButton>
                  </div>
                </div>
              </div>
            </CTCard.Content>
          </CTCard >
        </div>
      )}
      {/* Success: Display censored email */}
      {!isLoading && censoredEmail && (
        <div className={"ct-flex ct-justify-center ct-items-center cardContainer"}>
          <CTCard expandable={false} className='ct-px-1 ct-mb-4'>
            <CTCard.Header className="ct-px-4 ct-py-2 ct-border-t-[5px] ct-border-t-primary-500 ct-bg-cloudy-gray-500 ct-h-[75px]">
              <div className={"ct-flex ct-justify-between ct-items-center"} >
                <h1 className="ct-text-primary-500 ct-text-[22px] ct-font-bold ct-m-0 ct-leading-[1.5] ct-font-rock ct-text-nowrap">
                  {"Username Found"}
                </h1>
              </div>
            </CTCard.Header>
            <CTCard.Content className='ct-font-sans'>
              <div className="ct-p-4 ct-space-y-4" aria-live="polite">
                <p className="ct-text-[1rem]">
                  Your username (email) associated with this account is:
                </p>
                <p className="ct-text-[1.25rem] ct-font-bold ct-text-center ct-py-4">
                  {censoredEmail}
                </p>
                <div className="ct-flex ct-justify-center">
                  <CTButton
                    onClick={() => navigate("/")}
                    primary
                    raised>Login Now</CTButton>
                </div>
              </div>
            </CTCard.Content>
          </CTCard>
        </div>
      )}
      {requestError &&
        <div className="ct-flex ct-justify-center ct-items-center ct-px-1" role="alert" aria-live="assertive">
          <div className="ct-text-center ct-max-w-[775px] ct-mb-4">
            <p className="ct-font-bold ct-text-roman-red-500">
              {requestErrorMsg}
            </p>
          </div>
        </div>
      }
      {isLoading && (
        <div
          className="ct-fixed ct-top-0 ct-left-0 ct-right-0 ct-bottom-0 ct-flex ct-items-center ct-justify-center ct-bg-white ct-z-50 ct-overflow-hidden"
          role="status"
          aria-live="assertive"
        >
          <div className="ct-items-center ct-justify-center ct-m-4">
            <p className="ct-text-center ct-text-storm-gray-700 ct-font-bold ct-font-sans">
              {"We're looking up your account - please wait."}
            </p>
            <br />
            <p className="ct-text-center ct-text-storm-gray-700 ct-font-sans">
              {"Please don't close or refresh this window."}
            </p>
            <div className="ct-flex ct-items-center ct-justify-center ct-text-primary-500 ct-mt-4">
              <CTProgressCircular />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ForgotUsernameForm;
